import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface IStockOwner {
  id: number;
  firstName?: string;
  lastName?: string;
  phone?: string;
  name?: string;
  code?: string;
  [key: string]: any;
}

interface IClient {
  id: number;
  name: string;
  code: string;
  [key: string]: any;
}

interface IProduct {
  id: number;
  name: string;
  code: string;
  quantity: number;
  [key: string]: any;
}

interface IPallete {
  id: number;
  paleteCode?: string;
  paleteName?: string;
  productCode?: string;
  productName?: string;
  productShortDescription?: string;
  qtyPieces?: number;
  qtyCartons?: number;
  paleteRegisteredAt?: string;
  paleteUnit?: string;
  daysInStock?: number;
  quantity?: number;
  selectedQtyPieces?: number;
  [key: string]: any;
}

interface IShipping {
  address: string;
  stateId: number | null;
  cityId: number | null;
  zipCode?: string;
  deliveryDate?: string;
  notes?: string;
  [key: string]: any;
}

interface OrderFlowState {
  selectedStockOwner: IStockOwner | null;
  selectedClient: IClient | null;
  selectedProducts: IProduct[];
  selectedPalletes: IPallete[];
  shipping: IShipping | null;
  isClientOrder: boolean; // true = Client Order, false = Stock Order
  attachmentSessions: Record<string, number | null>; // Multi-instance session IDs keyed by instanceKey
}

const initialState: OrderFlowState = {
  selectedStockOwner: null,
  selectedClient: null,
  selectedProducts: [],
  selectedPalletes: [],
  shipping: null,
  isClientOrder: true, // Default to Client Order
  attachmentSessions: {},
};

export const orderFlowSlice = createSlice({
  name: 'orderFlow',
  initialState,
  reducers: {
    setStockOwner: (state, action: PayloadAction<IStockOwner>) => {
      state.selectedStockOwner = action.payload;
    },
    clearStockOwner: (state) => {
      state.selectedStockOwner = null;
    },
    setClient: (state, action: PayloadAction<IClient>) => {
      state.selectedClient = action.payload;
    },
    clearClient: (state) => {
      state.selectedClient = null;
    },
    addProduct: (state, action: PayloadAction<IProduct>) => {
      const existingIndex = state.selectedProducts.findIndex(p => p.id === action.payload.id);
      if (existingIndex === -1) {
        // Product doesn't exist - add it
        state.selectedProducts.push({ ...action.payload, quantity: 1 });
      } else {
        // Product exists - update it but preserve quantity
        const existingQuantity = state.selectedProducts[existingIndex].quantity;
        state.selectedProducts[existingIndex] = { ...action.payload, quantity: existingQuantity };
      }
    },
    setProducts: (state, action: PayloadAction<IProduct[]>) => {
      state.selectedProducts = action.payload;
    },
    removeProduct: (state, action: PayloadAction<number>) => {
      state.selectedProducts = state.selectedProducts.filter(p => p.id !== action.payload);
    },
    updateProductQuantity: (state, action: PayloadAction<{ id: number; quantity: number }>) => {
      const product = state.selectedProducts.find(p => p.id === action.payload.id);
      if (product) {
        product.quantity = action.payload.quantity;
      }
    },
    clearProducts: (state) => {
      state.selectedProducts = [];
    },
    addPallete: (state, action: PayloadAction<IPallete | IPallete[]>) => {
      const palletes = Array.isArray(action.payload) ? action.payload : [action.payload];
      palletes.forEach(pallete => {
        const existingIndex = state.selectedPalletes.findIndex(p => p.id === pallete.id);
        if (existingIndex === -1) {
          state.selectedPalletes.push({ ...pallete, quantity: pallete.quantity || 1 });
        }
      });
    },
    setPalletes: (state, action: PayloadAction<IPallete[]>) => {
      state.selectedPalletes = action.payload;
    },
    removePallete: (state, action: PayloadAction<number>) => {
      state.selectedPalletes = state.selectedPalletes.filter(p => p.id !== action.payload);
    },
    updatePalleteQuantity: (state, action: PayloadAction<{ id: number; quantity: number }>) => {
      const pallete = state.selectedPalletes.find(p => p.id === action.payload.id);
      if (pallete) {
        pallete.quantity = action.payload.quantity;
      }
    },
    updatePalleteQtyPieces: (state, action: PayloadAction<{ id: number; selectedQtyPieces: number }>) => {
      const pallete = state.selectedPalletes.find(p => p.id === action.payload.id);
      if (pallete) {
        pallete.selectedQtyPieces = action.payload.selectedQtyPieces;
      }
    },
    clearPalletes: (state) => {
      state.selectedPalletes = [];
    },
    setShipping: (state, action: PayloadAction<IShipping>) => {
      state.shipping = action.payload;
    },
    updateShippingField: (state, action: PayloadAction<{ field: keyof IShipping; value: any }>) => {
      if (!state.shipping) {
        // Initialize shipping if it's null
        state.shipping = {
          address: '',
          stateId: null,
          cityId: null,
          zipCode: '',
          deliveryDate: '',
          notes: '',
        };
      }
      state.shipping = { ...state.shipping, [action.payload.field]: action.payload.value };
    },
    clearShipping: (state) => {
      state.shipping = null;
    },
    setIsClientOrder: (state, action: PayloadAction<boolean>) => {
      state.isClientOrder = action.payload;
    },
    setAttachmentSessionId: (state, action: PayloadAction<{ key: string; id: number }>) => {
      state.attachmentSessions[action.payload.key] = action.payload.id;
    },
    clearAttachmentSessionId: (state, action: PayloadAction<string>) => {
      delete state.attachmentSessions[action.payload];
    },
    resetFlow: () => initialState,
  },
});

export const {
  setStockOwner,
  clearStockOwner,
  setClient,
  clearClient,
  addProduct,
  setProducts,
  removeProduct,
  updateProductQuantity,
  clearProducts,
  addPallete,
  setPalletes,
  removePallete,
  updatePalleteQuantity,
  updatePalleteQtyPieces,
  clearPalletes,
  setShipping,
  updateShippingField,
  clearShipping,
  setIsClientOrder,
  setAttachmentSessionId,
  clearAttachmentSessionId,
  resetFlow,
} = orderFlowSlice.actions;

export default orderFlowSlice.reducer;

